import React from 'react';
import { Star, Zap } from 'lucide-react';
import type { PlanRecommendationResult, SubscriptionTier } from '@court-sync/shared';
import { formatPlanTierLabel } from '../../utils/planRecommendationUi';

export interface PlanRecommendationBannerProps {
  recommendation: PlanRecommendationResult;
  selectedTier?: SubscriptionTier;
}

export function PlanRecommendationBanner({
  recommendation,
  selectedTier,
}: PlanRecommendationBannerProps) {
  const tier = selectedTier ?? recommendation.recommendedTier;

  return (
    <div
      className="mb-6 rounded-xl border border-primary-200 bg-primary-50/60 p-4 dark:border-primary-700/60 dark:bg-primary-500/10"
      data-testid="plan-recommendation-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-500/15">
          {tier === 'starter' ? (
            <Star className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden />
          ) : (
            <Zap className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-primary-800 dark:text-primary-200">
            Recommended for your academy
          </p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {formatPlanTierLabel(recommendation.recommendedTier)}
          </p>
          {recommendation.reasonLabels.length > 0 ? (
            <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
              {recommendation.reasonLabels.map((label) => (
                <li key={label} className="flex gap-2">
                  <span className="text-primary-600 dark:text-primary-400" aria-hidden>
                    •
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Starter fits solo coaches and owner-operated academies that enroll students manually and
              accept public booking requests without member accounts.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
